import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "react-toastify";
import { useFormik } from "formik";
import * as Yup from "yup";
import api from "../services/api";
import { useAuth } from "../context/AuthContext";
import {
  ArrowBack,
  Upload,
  Description,
  Delete,
  CloudUpload,
} from "@mui/icons-material";
import Button from "../components/ui/Button";
import Input from "../components/ui/Input";

const validationSchema = Yup.object({
  title: Yup.string().required("Title is required"),
  document_type: Yup.string().required("Document type is required"),
  category: Yup.string().required("Category is required"),
  description: Yup.string(),
  expiry_date: Yup.string().nullable(),
});

export default function DocumentUpload() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const userRole = user?.role?.toUpperCase() || user?.role;
  const isEmployee = userRole === "EMPLOYEE" || user?.role === "employee";
  const isAdmin = userRole === "ADMIN" || userRole === "HR";
  const isManager = userRole === "MANAGER" || user?.role === "manager";
  const canSelectEmployee = isAdmin || isManager; // Both admins and managers can select employees

  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [currentEmployee, setCurrentEmployee] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (canSelectEmployee) {
      fetchEmployees();
    } else if (isEmployee) {
      fetchCurrentEmployee();
    } else {
      setLoading(false);
    }
  }, [canSelectEmployee, isEmployee]);

  const fetchEmployees = async () => {
    try {
      const response = await api.get("/employees");
      const payload = response.data;
      const employeeList = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
        ? payload.data
        : [];
      
      // Find current user's employee record if they have one
      const currentUserId = user?.id || user?._id;
      const currentUserEmployee = employeeList.find(
        (e) => (e.user_id?.id || e.user_id?._id || e.user_id)?.toString() === currentUserId?.toString()
      );
      
      if (currentUserEmployee) {
        setCurrentEmployee(currentUserEmployee);
      }
      
      setEmployees(employeeList);
    } catch (err) {
      console.error("Failed to fetch employees", err);
      toast.error("Failed to load employees");
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentEmployee = async () => {
    try {
      const response = await api.get("/employees");
      const payload = response.data;
      const employeeList = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
        ? payload.data
        : [];
      const currentUserId = user?.id || user?._id;
      const emp = employeeList.find(
        (e) => (e.user_id?.id || e.user_id?._id || e.user_id)?.toString() === currentUserId?.toString()
      );
      setCurrentEmployee(emp);
    } catch (err) {
      console.error("Failed to fetch current employee", err);
      toast.error("Failed to load employee data");
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      // Validate file type
      const allowedTypes = [
        "application/pdf",
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
        "image/webp",
      ];

      if (!allowedTypes.includes(selectedFile.type)) {
        toast.error("Please upload a PDF or image file (JPEG, PNG, GIF, WebP)");
        return;
      }

      // Validate file size (10MB)
      if (selectedFile.size > 10 * 1024 * 1024) {
        toast.error("File size must be less than 10MB");
        return;
      }

      setFile(selectedFile);

      // Create preview for images
      if (selectedFile.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setFilePreview(reader.result);
        };
        reader.readAsDataURL(selectedFile);
      } else {
        setFilePreview(null);
      }
    }
  };

  const handleRemoveFile = () => {
    setFile(null);
    setFilePreview(null);
  };

  const uploadFile = async (fileToUpload) => {
    const formData = new FormData();
    formData.append("file", fileToUpload);

    try {
      // Don't set Content-Type header - axios will set it automatically with boundary
      const response = await api.post("/documents/upload-file", formData);

      if (response.data?.file_url) {
        return {
          file_url: response.data.file_url,
          file_name: response.data.filename || fileToUpload.name,
          file_size: response.data.file_size || fileToUpload.size,
          mime_type: response.data.mime_type || fileToUpload.type,
        };
      }
      throw new Error("No file URL returned");
    } catch (err) {
      console.error("File upload error:", err);
      throw new Error(err.response?.data?.error || "Failed to upload file");
    }
  };

  const formik = useFormik({
    initialValues: {
      title: "",
      document_type: "other",
      category: "hr",
      description: "",
      expiry_date: "",
      employee_id: isEmployee 
        ? (currentEmployee?.id || currentEmployee?._id || "") 
        : canSelectEmployee && currentEmployee
        ? (currentEmployee?.id || currentEmployee?._id || "")
        : "",
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      if (!file) {
        toast.error("Please select a file to upload");
        setSubmitting(false);
        return;
      }

      try {
        setUploading(true);

        // Upload file first
        const fileData = await uploadFile(file);

        // Prepare document data
        const documentData = {
          title: values.title,
          document_type: values.document_type,
          category: values.category,
          description: values.description || null,
          expiry_date: values.expiry_date || null,
          file_url: fileData.file_url,
          file_name: fileData.file_name,
          file_size: fileData.file_size,
          mime_type: fileData.mime_type,
          employee_id: isEmployee
            ? (currentEmployee?.id || currentEmployee?._id)
            : canSelectEmployee
            ? values.employee_id
            : null,
        };

        // Create document
        await api.post("/documents", documentData);

        toast.success("Document uploaded successfully!");
        navigate("/documents");
      } catch (err) {
        console.error("Upload error:", err);
        toast.error(err.response?.data?.error || err.message || "Failed to upload document");
      } finally {
        setUploading(false);
        setSubmitting(false);
      }
    },
  });

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="card animate-pulse p-8">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        {/* Header */}
        <div className="mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-primary mb-2">
              Upload Document
            </h1>
            <p className="text-sm sm:text-base text-gray-600">
              {isEmployee 
                ? "Upload your document" 
                : canSelectEmployee 
                ? "Upload a document for an employee or yourself"
                : "Upload a document"}
            </p>
          </div>
          <Button
            variant="secondary"
            onClick={() => navigate("/documents")}
            className="flex items-center justify-center gap-2 w-full sm:w-auto"
          >
            <ArrowBack className="w-4 h-4 sm:w-5 sm:h-5" />
            <span className="hidden sm:inline">Back</span>
            <span className="sm:hidden">Back</span>
          </Button>
        </div>

        {/* Form */}
        <form onSubmit={formik.handleSubmit} className="card p-4 sm:p-6">
          {/* Employee Selection (Admin and Manager) */}
          {canSelectEmployee && (
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Employee <span className="text-red-500">*</span>
              </label>
              <select
                name="employee_id"
                value={formik.values.employee_id}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="input-field w-full"
                required
              >
                <option value="">Select Employee</option>
                {employees.map((emp) => {
                  const empId = emp.id || emp._id;
                  const empName = emp.user_id?.name || "Unknown";
                  const isCurrentUser = currentEmployee && (empId === (currentEmployee.id || currentEmployee._id));
                  return (
                    <option
                      key={empId}
                      value={empId}
                    >
                      {empName}{isCurrentUser ? " (You)" : ""}
                    </option>
                  );
                })}
              </select>
              {formik.touched.employee_id && formik.errors.employee_id && (
                <p className="text-red-500 text-sm mt-1">{formik.errors.employee_id}</p>
              )}
              {currentEmployee && (
                <p className="text-sm text-gray-500 mt-1">
                  You can select your own name to upload a document for yourself
                </p>
              )}
            </div>
          )}

          {/* Title */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Title <span className="text-red-500">*</span>
            </label>
            <Input
              type="text"
              name="title"
              value={formik.values.title}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Enter document title"
              required
            />
            {formik.touched.title && formik.errors.title && (
              <p className="text-red-500 text-sm mt-1">{formik.errors.title}</p>
            )}
          </div>

          {/* Document Type */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Document Type <span className="text-red-500">*</span>
            </label>
            <select
              name="document_type"
              value={formik.values.document_type}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className="input-field w-full"
              required
            >
              <option value="other">Other</option>
              <option value="id_proof">ID Proof</option>
              <option value="address_proof">Address Proof</option>
              <option value="educational_certificate">Educational Certificate</option>
              <option value="employment_contract">Employment Contract</option>
              <option value="nda">NDA</option>
              <option value="policy_document">Policy Document</option>
            </select>
            {formik.touched.document_type && formik.errors.document_type && (
              <p className="text-red-500 text-sm mt-1">{formik.errors.document_type}</p>
            )}
          </div>

          {/* Category */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Category <span className="text-red-500">*</span>
            </label>
            <select
              name="category"
              value={formik.values.category}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className="input-field w-full"
              required
            >
              <option value="hr">HR</option>
              <option value="finance">Finance</option>
              <option value="compliance">Compliance</option>
              <option value="legal">Legal</option>
            </select>
            {formik.touched.category && formik.errors.category && (
              <p className="text-red-500 text-sm mt-1">{formik.errors.category}</p>
            )}
          </div>

          {/* Description */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Description
            </label>
            <textarea
              name="description"
              value={formik.values.description}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Enter document description (optional)"
              rows="4"
              className="input-field w-full"
            />
            {formik.touched.description && formik.errors.description && (
              <p className="text-red-500 text-sm mt-1">{formik.errors.description}</p>
            )}
          </div>

          {/* Expiry Date */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Expiry Date (Optional)
            </label>
            <Input
              type="date"
              name="expiry_date"
              value={formik.values.expiry_date}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.expiry_date && formik.errors.expiry_date && (
              <p className="text-red-500 text-sm mt-1">{formik.errors.expiry_date}</p>
            )}
          </div>

          {/* File Upload */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              File <span className="text-red-500">*</span>
            </label>
            {!file ? (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 sm:p-8 text-center hover:border-primary transition-colors">
                <input
                  type="file"
                  id="file-upload"
                  accept=".pdf,.jpg,.jpeg,.png,.gif,.webp"
                  onChange={handleFileChange}
                  className="hidden"
                />
                <label
                  htmlFor="file-upload"
                  className="cursor-pointer flex flex-col items-center gap-3 sm:gap-4"
                >
                  <CloudUpload className="w-10 h-10 sm:w-12 sm:h-12 text-gray-400" />
                  <div className="text-sm sm:text-base">
                    <span className="text-primary font-semibold">Click to upload</span> or drag and drop
                  </div>
                  <p className="text-xs sm:text-sm text-gray-500">
                    PDF, JPEG, PNG, GIF, WebP (Max 10MB)
                  </p>
                </label>
              </div>
            ) : (
              <div className="border border-gray-300 rounded-lg p-3 sm:p-4">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                    <Description className="w-6 h-6 sm:w-8 sm:h-8 text-primary flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-gray-700 truncate text-sm sm:text-base">{file.name}</p>
                      <p className="text-xs sm:text-sm text-gray-500">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleRemoveFile}
                    className="text-red-500 hover:text-red-700 flex-shrink-0"
                  >
                    <Delete className="w-4 h-4 sm:w-5 sm:h-5" />
                  </button>
                </div>
                {filePreview && (
                  <div className="mt-3 sm:mt-4">
                    <img
                      src={filePreview}
                      alt="Preview"
                      className="max-w-full max-h-48 sm:max-h-64 rounded-lg mx-auto"
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Submit Button */}
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-end">
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate("/documents")}
              disabled={uploading}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={uploading || !file}
              className="flex items-center justify-center gap-2 w-full sm:w-auto"
            >
              {uploading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  Upload Document
                </>
              )}
            </Button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

